import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, PackagePlus } from "lucide-react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Textarea } from "@nosible/shared/components/ui/textarea";
import { trpc } from "@/admin/lib/trpc";

function generateProductId(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 64);
}

export function AdminPlanCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [description, setDescription] = useState("");
  const [apiKeyLimit, setApiKeyLimit] = useState("10");

  const createMutation = trpc.admin.createPlan.useMutation({
    onSuccess: (product) => navigate(`/plans/${product.id}`),
  });

  const handleNameChange = (value: string) => {
    setName(value);
    if (!id || id === generateProductId(name)) {
      setId(generateProductId(value));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const parsedApiKeyLimit = Number.parseInt(apiKeyLimit, 10);
    if (!name.trim() || !id.trim() || Number.isNaN(parsedApiKeyLimit)) return;

    createMutation.mutate({
      id: id.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      defaultApiKeyLimit: parsedApiKeyLimit,
    });
  };

  return (
    <div className="admin-form-page">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/plans">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to products
        </Link>
      </Button>

      <div className="admin-form-page__heading">
        <PackagePlus aria-hidden="true" />
        <div>
          <span>PRODUCTS / CREATE</span>
          <h1>Create a product</h1>
          <p>
            Create an invoice-led access product. Configure its features and rate
            limits on the next screen.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Product definition</CardTitle>
          <CardDescription>
            Products created here are local access templates and do not initiate
            Stripe billing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="admin-entity-form" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="product-name">Product name</Label>
              <Input
                id="product-name"
                value={name}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder="Enterprise Data Access"
                disabled={createMutation.isPending}
                required
              />
            </div>

            <div>
              <Label htmlFor="product-id">Product ID</Label>
              <Input
                id="product-id"
                value={id}
                onChange={(event) =>
                  setId(
                    event.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "")
                      .substring(0, 64),
                  )
                }
                placeholder="enterprise-data-access"
                disabled={createMutation.isPending}
                required
              />
              <small>Permanent identifier used by subscriptions and API access.</small>
            </div>

            <div className="admin-entity-form__wide">
              <Label htmlFor="product-description">Description</Label>
              <Textarea
                id="product-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Who this product is for and what access it provides."
                disabled={createMutation.isPending}
              />
            </div>

            <div>
              <Label htmlFor="api-key-limit">Default API-key allowance</Label>
              <Input
                id="api-key-limit"
                type="number"
                min="0"
                max="10000"
                value={apiKeyLimit}
                onChange={(event) => setApiKeyLimit(event.target.value)}
                disabled={createMutation.isPending}
                required
              />
            </div>

            {createMutation.error && (
              <p className="admin-form-error">{createMutation.error.message}</p>
            )}

            <div className="admin-entity-form__actions">
              <Button variant="outline" asChild>
                <Link to="/plans">Cancel</Link>
              </Button>
              <Button
                className="brand-primary-button"
                type="submit"
                disabled={
                  createMutation.isPending ||
                  !name.trim() ||
                  !id.trim() ||
                  !apiKeyLimit
                }
              >
                {createMutation.isPending ? "Creating…" : "Create product"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
